/* Page lifecycle — Barba.js client-side navigation, the reference's own
   architecture (scripts-2026.js barba.init), ported 1:1 with the GSAP
   timeline rewritten in WAAPI like every other port in this theme.

   Two parts:

   1. Onni registry — because Barba swaps the [data-barba="container"] without
      a real page load, every script that touches elements INSIDE the
      container re-registers an init function here instead of running once as
      an IIFE. Each init receives { signal } (an AbortSignal): pass it to
      addEventListener / matchMedia listeners, and listen for 'abort' to stop
      rAF loops, intervals and IntersectionObservers. On every Barba `after`
      the previous page's signal aborts and all inits re-run against the new
      DOM — the reference's globalScripts() call, made leak-proof.
      Persistent scripts (menu, cart, signup, lenis, preloader — all outside
      the container) stay plain IIFEs and are untouched by navigation.

   2. Barba orchestration — their exact transition: the leaving container
      dims to 0.4 and sags 20vh while the incoming one (pinned fixed on top)
      wipes open top→down via clip-path (0vh → 100vh) with a -20vh → 0
      settle. 1s, their ease-3. sync: true — both run simultaneously.
      Their hooks, ported:
        before      → scroll-lock <html> (the lock doubles as Lenis' pause
                      signal — their lenis.stop())
        enter       → pin the incoming container fixed on top (their
                      gsap.set position:fixed)
        after       → shut the overlays ('onni:page:out', by then clipped away),
                      unpin (position:relative), scrollTop(y), release the
                      lock, swap title/ground, re-run inits (globalScripts),
                      nudge Shopify analytics (their resetShopify, minus the
                      jQuery-era glue our theme never needed)

   Decorates, never gates: no Barba / reduced motion / theme editor → normal
   full-page navigation, everything still works. */

(function () {
  'use strict';

  /* ---------------- 1. the registry ---------------- */

  var registry = [];
  var controller = new AbortController();

  window.Onni = window.Onni || {};

  window.Onni.register = function (name, fn) {
    for (var i = 0; i < registry.length; i += 1) {
      if (registry[i].name === name) {
        // re-registered (theme editor re-injecting a script) — replace + run
        registry[i].fn = fn;
        runInit(registry[i]);
        return;
      }
    }
    var item = { name: name, fn: fn };
    registry.push(item);
    runInit(item); // first registration = the initial page load
  };

  function runInit(item) {
    try {
      item.fn({ signal: controller.signal });
    } catch (e) {
      /* one broken section must never take down the rest of the page */
      if (window.console) console.error('[onni] init failed: ' + item.name, e);
    }
  }

  window.Onni.initPage = function () {
    controller.abort();
    controller = new AbortController();
    registry.forEach(runInit);
  };

  // theme editor: section re-renders replace DOM — rebind everything
  document.addEventListener('shopify:section:load', function () {
    window.Onni.initPage();
  });

  /* ---------------- 2. Barba ---------------- */

  if (!window.barba) return; // lib missing → plain navigation, no harm
  if (window.Shopify && window.Shopify.designMode) return; // theme editor
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (!document.querySelector('[data-barba="wrapper"]')) return;

  var EASE = 'cubic-bezier(0.65, 0.01, 0.05, 0.99)'; // their ease-3
  var DUR = 1000;

  // routes that leave the themed world — always full navigations
  var HARD_ROUTES = /^\/(checkout|cart$|cart\/|account|policies|challenge|password|admin|apps\/|a\/|cdn\/|gift_cards)/;

  /* ---------- scroll memory ----------
     The browser's own popstate restoration happens in the SAME document —
     it yanks the LEAVING page to the target entry's stored offset before the
     transition even starts (the visible "snap" on back-nav). Take it over:
     restoration goes manual, we save each page's position ourselves and put
     it back on back/forward. Forward navigations land at the top, like the
     reference. */
  if ('scrollRestoration' in window.history) {
    window.history.scrollRestoration = 'manual';
  }

  var SCROLL_KEY = 'onni-scroll:';
  var currentPath = window.location.pathname + window.location.search;

  function saveScroll(path, y) {
    try { window.sessionStorage.setItem(SCROLL_KEY + path, String(Math.round(y))); } catch (e) {}
  }
  function readScroll(path) {
    try { return parseInt(window.sessionStorage.getItem(SCROLL_KEY + path), 10) || 0; } catch (e) { return 0; }
  }

  function pathOf(href) {
    try {
      var u = new URL(href, window.location.origin);
      return u.pathname + u.search;
    } catch (e) {
      return window.location.pathname + window.location.search;
    }
  }

  // where the incoming page must land — resolved in `enter` (so the wipe can
  // already reveal that spot) and re-used in `after` for the real scroll
  var pendingScroll = 0;

  // full loads leave through here (checkout hop, reload) — keep the store warm
  window.addEventListener('pagehide', function () {
    saveScroll(window.location.pathname + window.location.search, window.scrollY);
  });

  // manual restoration also disables the browser's restore on FULL-load
  // back/forward and reloads (e.g. returning from checkout) — best-effort
  // restore from our own store instead
  try {
    var navEntry = performance.getEntriesByType('navigation')[0];
    if (navEntry && (navEntry.type === 'back_forward' || navEntry.type === 'reload')) {
      var y0 = readScroll(currentPath);
      if (y0 > 0) window.scrollTo(0, y0);
    }
  } catch (e) { /* older browsers: land at top, fine */ }

  /* Overlays (menu / cart / signup) live outside the container and persist
     across a swap. They used to snap shut in `before`, which read as the menu
     closing first and the transition starting from the bare old page. They now
     stay put and get WIPED AWAY by the incoming page instead, and are told to
     shut in `after` — by then hidden, in the same frame the container is
     unpinned, so the close is never seen. Nothing restores them afterwards, so
     a later back/forward returns to the plain page, not to an open menu.

     The overlay is cleared by clipping IT away from the top in lockstep with
     the wipe, not by lifting the container over it. Lifting worked but buried
     the header: .onni-header and .onni-menu share one `.shopify-section`
     wrapper (position:fixed, z-index:10 — a single stacking context), so
     anything that covers the menu covers the header with it. Clipping keeps
     the natural order — header over container over overlay — so the header
     stays visible throughout, exactly like an ordinary navigation. */
  var OVERLAY_SEL = '[data-menu].is-open, .onni-cart.is-open, [data-signup].is-open';
  var overlayWasOpen = false;
  var overlayAnims = [];

  barba.hooks.before(function () {
    // remember where the leaving page was, for a later back/forward return
    saveScroll(currentPath, window.scrollY);
    overlayWasOpen = !!document.querySelector(OVERLAY_SEL);
    // their lenis.stop(): the shared scroll-lock contract pauses Lenis
    document.documentElement.style.overflow = 'hidden';
  });

  barba.hooks.enter(function (data) {
    // their gsap.set(data.next.container, { position:fixed, top:0, left:0, width:100% })
    // — with one addition: on a back/forward return the container is pinned at
    // top:-y instead of 0, so the wipe reveals the page ALREADY at the spot the
    // visitor left it. The clip strip in the transition is offset by the same y
    // (it is measured from the element's own top), and the after-hook's
    // scrollTo(y) then lands on exactly what is on screen — no jump.
    var next = data.next.container;
    var s = next.style;
    s.position = 'fixed';
    s.left = '0';
    s.width = '100%';
    s.top = '0';

    var isHistory = data.trigger === 'back' || data.trigger === 'forward';
    var y = isHistory ? readScroll(pathOf(data.next.url && data.next.url.href)) : 0;
    // the stored offset can outrun a shorter page (resize, edited content)
    var max = Math.max(0, next.offsetHeight - window.innerHeight);
    pendingScroll = Math.min(y, max);

    if (pendingScroll > 0) s.top = -pendingScroll + 'px';
  });

  barba.hooks.after(function (data) {
    var next = data.next.container;

    // Shut the overlays now that the wipe has clipped them away, then drop the
    // clip — all in this one synchronous block, so no frame is painted in
    // between and the close (each handler flips `hidden`, no exit animation)
    // is never visible.
    if (overlayWasOpen) {
      document.dispatchEvent(new CustomEvent('onni:page:out'));
      overlayAnims.forEach(function (a) { a.cancel(); });
      overlayAnims = [];
      overlayWasOpen = false;
    }

    // their gsap.set(position: relative)
    next.style.position = 'relative';
    next.style.top = '';
    next.style.left = '';
    next.style.width = '';
    next.style.zIndex = '';

    // release the scroll lock BEFORE scrolling (overflow:hidden clamps
    // programmatic scrolls) → Lenis starts again via the shared observer
    document.documentElement.style.overflow = '';

    // head bits Barba doesn't manage: title + the page's ground colour
    try {
      var doc = new DOMParser().parseFromString(data.next.html, 'text/html');
      if (doc.title) document.title = doc.title;
    } catch (e) { /* keep the old title */ }
    document.documentElement.style.background = next.dataset.onniGround || '';

    if (window.OnniLenis && typeof window.OnniLenis.resize === 'function') {
      window.OnniLenis.resize();
    }

    // their $(window).scrollTop(0) — but trigger-aware: back/forward puts the
    // page back where the visitor left it, forward navigations start at top.
    // The offset was already resolved (and applied to the pin) in `enter`, so
    // this scroll matches what the wipe just revealed — nothing moves.
    currentPath = window.location.pathname + window.location.search;
    var y = pendingScroll;
    window.scrollTo(0, y);
    if (window.OnniLenis && typeof window.OnniLenis.scrollTo === 'function') {
      // sync Lenis' internal position too, or its next frame lerps us away
      window.OnniLenis.scrollTo(y, { immediate: true, force: true });
    }

    // their globalScripts() — rebind everything against the new container
    window.Onni.initPage();

    // their resetShopify(), reduced to what our theme actually needs:
    // a page-view ping so analytics keep counting virtual navigations
    try {
      if (window.ShopifyAnalytics && window.ShopifyAnalytics.lib && window.ShopifyAnalytics.lib.page) {
        window.ShopifyAnalytics.lib.page();
      }
    } catch (e) { /* analytics are never worth breaking navigation over */ }
  });

  barba.init({
    preventRunning: true,
    cacheFirstPage: true,
    timeout: 10000,
    prevent: function (data) {
      var href = data.href || '';
      var url;
      try {
        url = new URL(href, window.location.origin);
      } catch (e) {
        return true;
      }
      if (url.origin !== window.location.origin) return true;
      return HARD_ROUTES.test(url.pathname);
    },
    transitions: [
      {
        sync: true, // both containers animate simultaneously, like theirs
        enter: function (data) {
          var next = data.next.container;
          // hold the incoming page at the wipe's first frame (fully clipped)
          // while its priority photo gets a short head start — see heroReady
          next.style.marginTop = '-20vh';
          next.style.clipPath = 'polygon(0% ' + pendingScroll + 'px, 100% ' + pendingScroll + 'px, 100% ' +
            pendingScroll + 'px, 0% ' + pendingScroll + 'px)';
          return heroReady(next).then(function () { return runWipe(data); });
        }
      }
    ]
  });

  /* A wipe that opens onto a still-empty photo frame reads as "slow site".
     Give the incoming page's priority photo up to HERO_WAIT_MS to finish
     decoding before the wipe starts. Usually it is already cached (warmHero
     on hover / idle) and this resolves at once; on a cold tap it trades a
     sliver of start delay for a wipe that reveals a finished photo. Capped,
     so a slow network never holds navigation hostage. */
  var HERO_WAIT_MS = 250;

  function heroReady(container) {
    var hero = container.querySelector('img[fetchpriority="high"]');
    if (!hero || (hero.complete && hero.naturalWidth > 0)) return Promise.resolve();
    var decoded = typeof hero.decode === 'function'
      ? hero.decode().catch(function () {})
      : new Promise(function (r) { hero.addEventListener('load', r, { once: true }); });
    return Promise.race([
      decoded,
      new Promise(function (r) { setTimeout(r, HERO_WAIT_MS); })
    ]);
  }

  function runWipe(data) {
    var cur = data.current.container;
    var next = data.next.container;

    // their barbaTransitionTl, GSAP → WAAPI:
    // current: opacity 1 → 0.4, marginTop 0 → 20vh
    var out = cur.animate(
      [
        { opacity: 1, marginTop: '0vh' },
        { opacity: 0.4, marginTop: '20vh' }
      ],
      { duration: DUR, easing: EASE, fill: 'forwards' }
    );

    // next: marginTop -20vh → 0, clip strip 0vh → 100vh (top-down wipe).
    // The strip is measured from the container's own top, so when the
    // enter-hook pinned it at top:-y it starts y lower — the wipe then
    // opens over the restored scroll position instead of the page top.
    var off = pendingScroll;
    // the strip has to be expressed in px now that it carries an offset;
    // take the larger viewport measure so mobile (where innerHeight and
    // 100vh diverge over the URL bar) never leaves an unrevealed sliver
    var vh = Math.max(window.innerHeight, document.documentElement.clientHeight);
    var inn = next.animate(
      [
        {
          marginTop: '-20vh',
          clipPath: 'polygon(0% ' + off + 'px, 100% ' + off + 'px, 100% ' + off + 'px, 0% ' + off + 'px)'
        },
        {
          marginTop: '0vh',
          clipPath: 'polygon(0% ' + off + 'px, 100% ' + off + 'px, 100% ' + (off + vh) + 'px, 0% ' + (off + vh) + 'px)'
        }
      ],
      { duration: DUR, easing: EASE }
    );
    // the animation now owns these — drop the hold-frame set in `enter`
    next.style.marginTop = '';
    next.style.clipPath = '';

    /* Clip any open overlay away from the top, following the wipe's own
       leading edge so the two read as one movement. That edge sits at
       `clip progress + marginTop` = p·1.2vh − 0.2vh in viewport terms
       (both inputs share this easing), i.e. it travels linearly from
       −20vh to 100vh — hence these two keyframes. Same px basis as the
       strip above so they cannot drift apart. */
    if (overlayWasOpen) {
      var edge0 = Math.round(-0.2 * vh);
      document.querySelectorAll(OVERLAY_SEL).forEach(function (el) {
        overlayAnims.push(
          el.animate(
            [
              { clipPath: 'polygon(0% ' + edge0 + 'px, 100% ' + edge0 + 'px, 100% 100%, 0% 100%)' },
              { clipPath: 'polygon(0% ' + vh + 'px, 100% ' + vh + 'px, 100% 100%, 0% 100%)' }
            ],
            { duration: DUR, easing: EASE, fill: 'forwards' }
          )
        );
      });
    }

    return Promise.all([
      out.finished.catch(function () {}),
      inn.finished.catch(function () {})
    ]).then(function () {
      // their clearProps: 'all' — drop every animated value
      out.cancel();
      inn.cancel();
    });
  }

  /* Prefetch. The sync transition cannot start until the next page's HTML is
     in hand, and Barba only caches pages already visited — so a click on a
     not-yet-seen link sat on the network for a few hundred ms with zero
     feedback (Mats: "duurt net iets te lang voordat hij reageert").
     barba.prefetch() is the core's own public API — it dedupes via its cache
     and reuses the configured timeout/error handling, so it can fire freely.

     Two layers:
     1. eager, on idle after each page settles — warm EVERY eligible nav
        destination in the DOM before the visitor even points at it. This is
        what makes a quick header click instant: hover→click on a small
        top-corner link can be <80ms (measured), too short for hover prefetch
        to finish (154ms cold), but the page is already cached by then.
        Cheap here — a ~5-page single-product site — and skipped under
        Save-Data / 2g so a metered phone only pays for what it clicks.
     2. hover / touchstart — the fallback for anything not yet eager-warmed
        (a link added later, or before the idle pass ran). */
  function eligible(href) {
    var url;
    try { url = new URL(href, window.location.origin); } catch (err) { return null; }
    if (url.origin !== window.location.origin) return null;
    if (HARD_ROUTES.test(url.pathname)) return null;
    if (url.pathname === window.location.pathname && url.search === window.location.search) return null;
    return url.pathname + url.search;
  }

  /* The HTML alone isn't enough: the incoming page's priority photo (hero
     slide, product shot, article image) only started downloading once the
     swap had injected it — the wipe then revealed an empty frame that filled
     in late. So once the prefetched HTML lands, its img[fetchpriority=high]
     is fetched too (OnniWarm dedupes and skips Save-Data). */
  var heroWarmed = {};

  function warmHero(path) {
    if (heroWarmed[path] || !window.OnniWarm) return;
    var entry;
    try { entry = barba.cache.get(new URL(path, window.location.origin).href); } catch (err) { return; }
    if (!entry || !entry.request || typeof entry.request.then !== 'function') return;
    heroWarmed[path] = 1;
    entry.request.then(function (res) {
      window.OnniWarm.heroFromHTML(res && typeof res === 'object' ? res.html : res);
    }, function () { heroWarmed[path] = 0; });
  }

  function warm(href) {
    var path = eligible(href);
    if (!path) return;
    try { barba.prefetch(path); } catch (err) { return; /* never block a nav over this */ }
    warmHero(path);
  }

  function prefetchFromEvent(e) {
    var a = e.target && e.target.closest && e.target.closest('a[href]');
    if (!a || a.target || a.hasAttribute('download')) return;
    warm(a.href);
  }
  document.addEventListener('mouseover', prefetchFromEvent, { passive: true });
  document.addEventListener('touchstart', prefetchFromEvent, { passive: true });

  var conn = navigator.connection;
  var saveData = conn && (conn.saveData || /(^|-)2g$/.test(conn.effectiveType || ''));
  var idle = window.requestIdleCallback || function (fn) { return setTimeout(fn, 200); };

  function eagerPrefetch() {
    if (saveData) return; // metered connection: hover prefetch only
    idle(function () {
      // ONLY the persistent primary nav (the bar + the fullscreen menu) —
      // the handful of destinations where a quick click must feel instant
      // (Mats: the small header links). The long tail — footer legal, single
      // blog posts, product-variant links in content — stays on hover
      // prefetch, so a homepage load warms ~4 pages, not a dozen.
      var seen = {};
      var links = document.querySelectorAll('.onni-header a[href], .onni-menu a[href]');
      for (var i = 0; i < links.length; i++) {
        var a = links[i];
        if (a.target || a.hasAttribute('download')) continue;
        var path = eligible(a.href);
        if (!path || seen[path]) continue;
        seen[path] = 1;
        warm(path);
      }
    }, { timeout: 3000 });
  }

  // register() runs this immediately (first page, deferred to idle) and again
  // on every barba swap + theme-editor section reload, so each new page's
  // links get warmed. Fallback for the no-Barba path: run once directly.
  if (window.Onni && window.Onni.register) {
    window.Onni.register('eager-prefetch', function () { eagerPrefetch(); });
  } else {
    eagerPrefetch();
  }
})();


/* Real app demonstration: silent autoplay only while visible. A native control
   remains available for reduced motion, blocked autoplay and no-JS navigation. */
window.Onni.register('app-demo', function (ctx) {
  var videos = Array.from(document.querySelectorAll('[data-onni-demo] video'));
  if (!videos.length) return;
  var signal = ctx.signal;
  var motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var visible = new Set();
  function update(video) {
    video.muted = true;
    video.autoplay = !motion.matches && visible.has(video);
    if (!motion.matches && visible.has(video)) {
      video.play().then(function () { if (signal.aborted || !visible.has(video) || motion.matches) video.pause(); }).catch(function () {});
    } else { video.pause(); }
  }
  var observer = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (entry.isIntersecting && entry.intersectionRatio >= .25) visible.add(entry.target); else visible.delete(entry.target);
      update(entry.target);
    });
  }, { threshold: .25 });
  videos.forEach(function (video) { video.muted = true; observer.observe(video); });
  motion.addEventListener('change', function () { videos.forEach(update); }, { signal: signal });
  signal.addEventListener('abort', function () { observer.disconnect(); videos.forEach(function (video) { video.pause(); }); visible.clear(); }, { once: true });
});

/* A hypothetical time illustration, never measured or promised Onni results. */
window.Onni.register('room-for-life', function (ctx) {
  document.querySelectorAll('[data-onni-time-room]').forEach(function (root) {
    var input = root.querySelector('[data-room-input]');
    var minutes = root.querySelector('[data-room-minutes]');
    var hours = root.querySelector('[data-room-hours]');
    var announcement = root.querySelector('[data-room-announcement]');
    var control = root.querySelector('[data-room-control]');
    if (!input || !minutes || !hours || !control) return;
    function update(announce) {
      var value = Number(input.value);
      var annual = Math.round(value * 365 / 60);
      minutes.textContent = value;
      hours.textContent = annual;
      input.setAttribute('aria-valuetext', value + ' minutes a day, about ' + annual + ' hours a year');
      if (announce && announcement) announcement.textContent = value + ' minutes a day is about ' + annual + ' hours a year.';
    }
    control.hidden = false;
    update(false);
    input.addEventListener('input', function () { update(false); }, { signal: ctx.signal });
    input.addEventListener('change', function () { update(true); }, { signal: ctx.signal });
  });
});
